import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import classes from 'classnames';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { translatableResourcePath } from '../../utils/helpers';
import './numerical-scale.scss';
import { QuestionHeading } from './question-heading';
import {
    getMaxPositiveScore,
    getNumericalScaleOptionScores,
    isHighlightOnlyAnswer,
} from './answer-highlight';

function resolveUserReviewAnswer(answer) {
    if (!answer?.userAnswer) return {};
    const { userAnswer } = answer;
    if (userAnswer.value != null) {
        return { value: userAnswer.value, score: userAnswer.score };
    }
    return {};
}

function getOptionHighlightClass(optionScore, maxPositiveScore) {
    if (optionScore <= 0 || maxPositiveScore <= 0) {
        return null;
    }
    return optionScore >= maxPositiveScore
        ? 'positive-score-max'
        : 'positive-score-partial';
}

export const NumericalScale = observer(
    ({ ui, handleChange, value, answer }) => {
        const { t } = useTranslation('assessment');
        const [showLabels, setShowLabels] = React.useState(true);

        const handleToggle = (event, newValue) => {
            handleChange(newValue);
            if (newValue) {
                hideLabel();
            } else {
                showLabel();
            }
        };

        const showLabel = () => {
            setShowLabels(true);
        };

        const hideLabel = () => {
            setShowLabels(false);
        };

        useEffect(() => {
            showLabel();
        }, [ui]);

        const highlightOnly = isHighlightOnlyAnswer(answer);
        const userReview = resolveUserReviewAnswer(answer);
        const optionScores = useMemo(() => {
            if (!answer) return {};
            if (answer.optionScores) {
                return answer.optionScores;
            }
            if (highlightOnly && answer.correctAnswer) {
                const scores = {};
                Object.entries(answer.correctAnswer).forEach(
                    ([optionValue, entry]) => {
                        if (entry?.score != null) {
                            scores[optionValue] = entry.score;
                        }
                    }
                );
                return scores;
            }
            return getNumericalScaleOptionScores(
                answer.actions || { values: {} }
            );
        }, [answer, highlightOnly]);

        const maxPositiveScore = useMemo(
            () => getMaxPositiveScore(optionScores),
            [optionScores]
        );

        const reviewValue = highlightOnly ? null : userReview.value;
        const reviewScore = highlightOnly ? null : userReview.score;

        return (
            <div className={classes('assessment')}>
                <div className={'assessment-question'}>
                    {ui.image && (
                        <div className="image-fullwidth">
                            <img
                                src={translatableResourcePath(ui.image, t)}
                                alt="illustration"
                            />
                        </div>
                    )}

                    <QuestionHeading ui={ui} />
                    <ToggleButtonGroup
                        className={classes(
                            'numerical-scale ',
                            { showLabels: showLabels },
                            {
                                [`score-${reviewScore}`]:
                                    answer &&
                                    !highlightOnly &&
                                    userReview.value != null,
                            }
                        )}
                        onChange={handleToggle}
                        value={value || reviewValue || null}
                        exclusive
                        aria-label="numerical scale"
                    >
                        {ui.options.map((v, i) => {
                            const optionScore = optionScores[v.value] ?? 0;
                            const highlightClass = highlightOnly
                                ? getOptionHighlightClass(
                                      optionScore,
                                      maxPositiveScore
                                  )
                                : null;

                            return (
                                <ToggleButton
                                    className={classes(
                                        'numerical-scale-button',
                                        {
                                            'Mui-disabled': !!answer,
                                            answer: !!answer,
                                            question: !answer,
                                            [highlightClass]: Boolean(
                                                highlightClass
                                            ),
                                        }
                                    )}
                                    key={'select-' + i}
                                    aria-label={v.value}
                                    value={v.value}
                                >
                                    {' '}
                                    {t(v.text)}
                                    <label>{t(v.label)}</label>
                                </ToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>
                </div>
            </div>
        );
    }
);
